/**
 * Reactive-variable store for the per-pipeline activity log drawer (SE-5504).
 *
 * Mirrors `reviewQueueDrawerStore`: an Apollo `makeVar` so the board toolbar
 * can open the log without prop-drilling a callback through `TopPanelView`.
 *
 * Unlike the Review queue, the board scope here is REQUIRED in practice —
 * `pipelineActivityLog(kanbanBoardId: ID!)` has no whole-org form — so a null
 * board id simply leaves the drawer's read skipped.
 * @module features/player-kanban/stores/pipelineActivityLogDrawerStore
 */

import { makeVar } from "@apollo/client";

/** Serializable state of the pipeline activity log drawer. */
export interface PipelineActivityLogDrawerState {
  /** Whether the drawer is open. */
  readonly open: boolean;
  /** The pipeline whose audit log is shown. */
  readonly kanbanBoardId: string | null;
}

/** Initial (closed) state. */
const INITIAL_STATE: PipelineActivityLogDrawerState = {
  open: false,
  kanbanBoardId: null,
};

/**
 * Apollo reactive variable holding the activity-log drawer state.
 * @remarks Subscribe via `usePipelineActivityLogDrawerState`.
 */
export const pipelineActivityLogDrawerVar =
  makeVar<PipelineActivityLogDrawerState>(INITIAL_STATE);

/** Reset the drawer to its initial closed state. */
export function resetPipelineActivityLogDrawer(): void {
  pipelineActivityLogDrawerVar(INITIAL_STATE);
}
